import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useCreateChecklistTemplate, useUpdateChecklistTemplate } from './api';
import {
  CATEGORY_LABELS,
  ITEM_TYPE_LABELS,
  type ChecklistCategory,
  type ChecklistTemplate,
  type ItemType,
} from './types';

interface ItemDraft {
  key: string; // local id pra react key
  label: string;
  description: string;
  type: ItemType;
  required: boolean;
  photoRequired: boolean;
  group: string;
}

function newItem(): ItemDraft {
  return {
    key: Math.random().toString(36).slice(2, 10),
    label: '',
    description: '',
    type: 'ok_nok',
    required: true,
    photoRequired: false,
    group: '',
  };
}

interface ChecklistTemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Se fornecido, o dialog opera em modo edição. */
  template?: ChecklistTemplate | null;
}

export function ChecklistTemplateFormDialog({
  open,
  onOpenChange,
  template,
}: ChecklistTemplateFormDialogProps) {
  const isEdit = !!template;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ChecklistCategory>('daily_inspection');
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<ItemDraft[]>([newItem()]);

  useEffect(() => {
    if (!open) return;
    if (template) {
      setName(template.name);
      setDescription(template.description ?? '');
      setCategory(template.category);
      setIsActive(template.isActive);
      setItems(
        (template.items ?? []).length > 0
          ? template.items.map((it) => ({
              key: it.id,
              label: it.label,
              description: it.description ?? '',
              type: it.type,
              required: it.required,
              photoRequired: it.photoRequired,
              group: it.group ?? '',
            }))
          : [newItem()],
      );
    } else {
      setName('');
      setDescription('');
      setCategory('daily_inspection');
      setIsActive(true);
      setItems([newItem()]);
    }
  }, [open, template]);

  const create = useCreateChecklistTemplate();
  const update = useUpdateChecklistTemplate(template?.id ?? '');
  const pending = create.isPending || update.isPending;

  const updateItem = (key: string, patch: Partial<ItemDraft>) => {
    setItems((arr) => arr.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };
  const removeItem = (key: string) => {
    setItems((arr) => (arr.length === 1 ? arr : arr.filter((it) => it.key !== key)));
  };
  const addItem = () => setItems((arr) => [...arr, newItem()]);

  const validItems = items.filter((i) => i.label.trim().length > 0);
  const canSubmit =
    name.trim().length > 0 && validItems.length > 0 && !pending;

  const handleSubmit = async () => {
    const payloadItems = validItems.map((it, idx) => ({
      label: it.label.trim(),
      description: it.description.trim() || undefined,
      type: it.type,
      required: it.required,
      photoRequired: it.photoRequired,
      sortOrder: idx,
      group: it.group.trim() || undefined,
    }));
    try {
      if (isEdit && template) {
        await update.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          category,
          isActive,
          items: payloadItems,
        });
        toast.success(`Modelo "${name.trim()}" atualizado`);
      } else {
        await create.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          category,
          items: payloadItems,
        });
        toast.success(`Modelo "${name.trim()}" criado`);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar modelo de checklist' : 'Novo modelo de checklist'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Alterações afetarão apenas execuções futuras. Histórico é preservado.'
              : 'Defina os itens que motoristas precisarão responder em cada execução.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Cabeçalho */}
          <div className="space-y-1.5">
            <Label htmlFor="name">Nome do modelo *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Inspeção diária de veículo"
              maxLength={120}
              autoFocus
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Para que serve esse checklist?"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="category">Categoria *</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as ChecklistCategory)}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CATEGORY_LABELS) as ChecklistCategory[]).map((c) => (
                    <SelectItem key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isEdit && (
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="size-4 rounded border-border"
              />
              Modelo ativo (disponível para novas execuções)
            </label>
          )}

          {/* Itens */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Itens do checklist *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="size-4" />
                Adicionar item
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((item, idx) => (
                <div
                  key={item.key}
                  className="space-y-3 rounded-lg border border-border bg-card p-3"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground">
                      {idx + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input
                        value={item.label}
                        onChange={(e) => updateItem(item.key, { label: e.target.value })}
                        placeholder="Pergunta (ex.: Pneus em bom estado?)"
                        maxLength={255}
                      />
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(item.key, { description: e.target.value })}
                        placeholder="Descrição (opcional)"
                      />
                    </div>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive"
                        onClick={() => removeItem(item.key)}
                        title="Remover item"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-4">
                    <div>
                      <Label className="text-[11px] uppercase text-muted-foreground">
                        Tipo
                      </Label>
                      <Select
                        value={item.type}
                        onValueChange={(v) => updateItem(item.key, { type: v as ItemType })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(ITEM_TYPE_LABELS) as ItemType[]).map((t) => (
                            <SelectItem key={t} value={t}>
                              {ITEM_TYPE_LABELS[t]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase text-muted-foreground">
                        Grupo
                      </Label>
                      <Input
                        className="h-8"
                        value={item.group}
                        onChange={(e) => updateItem(item.key, { group: e.target.value })}
                        placeholder="Ex.: Motor"
                        maxLength={64}
                      />
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 self-end pb-1 text-xs">
                      <input
                        type="checkbox"
                        checked={item.required}
                        onChange={(e) => updateItem(item.key, { required: e.target.checked })}
                        className="size-4 rounded border-border"
                      />
                      Obrigatório
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 self-end pb-1 text-xs">
                      <input
                        type="checkbox"
                        checked={item.photoRequired}
                        onChange={(e) => updateItem(item.key, { photoRequired: e.target.checked })}
                        className="size-4 rounded border-border"
                      />
                      Foto obrigatória
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {validItems.length === 0 && (
              <p className="text-xs text-destructive">
                Pelo menos um item com pergunta preenchida é obrigatório.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {pending
              ? isEdit
                ? 'Salvando…'
                : 'Criando…'
              : isEdit
                ? 'Salvar alterações'
                : 'Criar modelo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
